import React, { useEffect, useState } from "react";
import { Trophy, ChevronDown, ChevronUp, Award } from "lucide-react";
import { api } from "../lib/api";

// Lifetime badge tiers — permanent achievement, based on total paid +
// deposit-held agreements closed. Never resets (matches the backend's
// BADGE_TIERS in server.py).
const BADGE_STYLES = {
  Metallic: "bg-slate-100 text-slate-600 border-slate-300",
  Bronze: "bg-amber-100 text-amber-800 border-amber-300",
  Silver: "bg-gray-100 text-gray-700 border-gray-300",
  Gold: "bg-yellow-100 text-yellow-800 border-yellow-400",
  Platinum: "bg-indigo-100 text-indigo-700 border-indigo-300",
  Diamond: "bg-cyan-100 text-cyan-700 border-cyan-300",
};

// Reminder-style banner showing the agent/broker's rank on the points
// leaderboard (50 points per referred tenant/landlord who completes KYC),
// scoped to their local area, plus their lifetime badge tier — with an
// expandable top-10 list.
export default function LeaderboardWidget() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard/points")
      .then(({ data }) => setData(data))
      .catch((e) => console.error("Failed to load leaderboard:", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  if (!data.my_area) {
    // No deals created yet — no local area to rank in.
    return (
      <div className="rounded-xl bg-gray-100 border border-gray-200 p-4 flex items-center gap-3">
        <Trophy className="h-5 w-5 text-gray-500 shrink-0" />
        <p className="text-sm text-gray-700">
          Close your first deal to join the local leaderboard and start earning points for every tenant/landlord you bring onboard.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-gray-100 border border-gray-200 overflow-hidden">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex items-center gap-3">
          <Trophy className="h-5 w-5 text-gray-500 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
              {data.my_rank ? `Rank #${data.my_rank}` : "Unranked"} in {data.my_area}
              {data.my_badge_tier && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${BADGE_STYLES[data.my_badge_tier] || ""}`}>
                  <Award className="h-3 w-3" /> {data.my_badge_tier}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-0.5">
              {data.my_points} points from {Math.round(data.my_points / 50)} tenants/landlords onboarded — out of {data.total_in_area} agents/brokers in your area.
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-4">
          {data.my_badge_next_tier && (
            <p className="text-xs text-gray-600">
              {data.my_deals_to_next_badge} more agreement{data.my_deals_to_next_badge === 1 ? "" : "s"} to reach{" "}
              <span className="font-semibold">{data.my_badge_next_tier}</span>.
            </p>
          )}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Top 10 in {data.my_area}
            </div>
            <div className="space-y-1">
              {data.top_10.map((row) => (
                <div
                  key={row.rank}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                    row.is_me ? "bg-white border border-gray-300 font-semibold" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-5 text-gray-500">{row.rank}</span>
                    <span className="text-gray-800">{row.name}</span>
                  </div>
                  <span className="text-gray-700 font-medium">{row.points} pts</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
